import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';

import { Company } from '../models';
import { CompanyService } from '../services/company.service';

/**
 * Resolver for Company contact routes.
 * Pre-loads company contact data before navigation completes.
 * 
 * Usage in routing:
 * {
 *   path: 'Company/:companyId',
 *   resolve: { company: companyResolver }
 * }
 * 
 * Access in component:
 * this.route.data.subscribe(data => {
 *   const company = data['company'];
 * });
 */
export const companyResolver: ResolveFn<Company.CompanySummary> = (route) => {
  const companyService = inject(CompanyService);
  const router = inject(Router);
  const companyId = route.paramMap.get('companyId');

  if (!companyId) {
    router.navigate(['/error'], {
      state: {
        title: 'Invalid Contact',
        message: 'No contact ID provided.',
        backRoute: ['/Contacts'],
        backLabel: 'Back to Contacts'
      }
    });
    return EMPTY;
  }

  return companyService.getSummary(companyId).pipe(
    catchError((error) => {
      router.navigate(['/error'], {
        state: {
          title: 'Contact Not Found',
          message: 'The company contact could not be loaded. Please try again or contact support if the issue persists.',
          backRoute: ['/Contacts'],
          backLabel: 'Back to Contacts'
        }
      });
      return EMPTY;
    })
  );
};
